import { intersectionOverUnion, relativeCenterDistance, box, type Box } from '../geometry/box';
import type { Detection } from '../detection/types';
import { smoothSamples, trackEnd, trackStart, type Track, type TrackKind, type TrackSample } from './track';

export interface TrackerOptions {
  /** Minimum IoU for an overlap based match. */
  readonly iouThreshold: number;
  /**
   * Fallback match for fast motion (low IoU between sparse samples): max center
   * distance relative to box size.
   */
  readonly maxRelativeDistance: number;
  /** A track that has not been seen for this long is closed. */
  readonly maxGapSec: number;
  /** Tracks with fewer samples are dropped unless they were seen with high confidence. */
  readonly minSamples: number;
  /** Score that makes even a single detection worth keeping (privacy first). */
  readonly strongScore: number;
}

export const DEFAULT_TRACKER_OPTIONS: TrackerOptions = {
  iouThreshold: 0.2,
  maxRelativeDistance: 1.2,
  maxGapSec: 1.0,
  minSamples: 2,
  strongScore: 0.8,
};

interface MutableTrack {
  id: string;
  kind: TrackKind;
  samples: TrackSample[];
  maxScore: number;
}

/** Linear extrapolation from the last two samples, capped to avoid runaway predictions. */
function predict(track: MutableTrack, time: number): Box {
  const last = track.samples.at(-1)!;
  const previous = track.samples.at(-2);
  if (!previous || last.t <= previous.t) return last.box;
  const dt = Math.min(time - last.t, 0.5);
  const k = dt / (last.t - previous.t);
  return box(
    last.box.x + (last.box.x - previous.box.x) * k,
    last.box.y + (last.box.y - previous.box.y) * k,
    last.box.w,
    last.box.h,
  );
}

function sizeRatioOk(a: Box, b: Box): boolean {
  const ratio = (a.w * a.h) / Math.max(b.w * b.h, 1e-12);
  return ratio > 0.3 && ratio < 3.3;
}

/**
 * Online multi-object tracker (IoU + motion prediction, ByteTrack-style greedy
 * association). Feed detections in time order with {@link update}, then call
 * {@link finish} to obtain cleaned, smoothed tracks.
 */
export class MultiObjectTracker {
  private readonly active: MutableTrack[] = [];
  private readonly closed: MutableTrack[] = [];
  private nextId = 1;
  private lastTime = -Infinity;

  constructor(private readonly options: TrackerOptions = DEFAULT_TRACKER_OPTIONS) {}

  update(time: number, detections: readonly Detection[]): void {
    if (time < this.lastTime) throw new RangeError('Tracker updates must be in time order');
    this.lastTime = time;
    this.closeStale(time);

    const candidates: Array<{ track: MutableTrack; detection: Detection; affinity: number }> = [];
    for (const track of this.active) {
      const predicted = predict(track, time);
      for (const detection of detections) {
        if (detection.kind !== track.kind) continue;
        const iou = intersectionOverUnion(predicted, detection.box);
        if (iou >= this.options.iouThreshold) {
          candidates.push({ track, detection, affinity: 1 + iou });
          continue;
        }
        const distance = relativeCenterDistance(predicted, detection.box);
        if (distance <= this.options.maxRelativeDistance && sizeRatioOk(predicted, detection.box)) {
          candidates.push({ track, detection, affinity: 1 - distance / this.options.maxRelativeDistance });
        }
      }
    }
    candidates.sort((a, b) => b.affinity - a.affinity);

    const usedTracks = new Set<MutableTrack>();
    const usedDetections = new Set<Detection>();
    for (const { track, detection } of candidates) {
      if (usedTracks.has(track) || usedDetections.has(detection)) continue;
      usedTracks.add(track);
      usedDetections.add(detection);
      track.samples.push({ t: time, box: detection.box, score: detection.score });
      track.maxScore = Math.max(track.maxScore, detection.score);
    }

    for (const detection of detections) {
      if (usedDetections.has(detection)) continue;
      this.active.push({
        id: `${detection.kind}-${this.nextId++}`,
        kind: detection.kind,
        samples: [{ t: time, box: detection.box, score: detection.score }],
        maxScore: detection.score,
      });
    }
  }

  /**
   * Tracks of `kind` that would survive {@link finish} if it ran now — a live
   * count close to the final result (raw detection counts are far higher).
   */
  confirmedCount(kind: TrackKind): number {
    let count = 0;
    for (const track of [...this.closed, ...this.active]) {
      if (track.kind === kind && (track.samples.length >= this.options.minSamples || track.maxScore >= this.options.strongScore)) count++;
    }
    return count;
  }

  finish(): Track[] {
    this.closed.push(...this.active.splice(0));
    return this.closed
      .filter((track) => track.samples.length >= this.options.minSamples || track.maxScore >= this.options.strongScore)
      .map((track) => ({ id: track.id, kind: track.kind, samples: smoothSamples(track.samples) }))
      .sort((a, b) => trackStart(a) - trackStart(b));
  }

  private closeStale(time: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const track = this.active[i]!;
      if (time - track.samples.at(-1)!.t > this.options.maxGapSec) {
        this.closed.push(track);
        this.active.splice(i, 1);
      }
    }
  }
}

/**
 * Joins fragments of the same object that were split by a longer occlusion or a
 * missed stretch, so users see one thumbnail per person/plate instead of many.
 */
export function stitchTracks(tracks: readonly Track[], maxGapSec = 2.5, maxRelativeDistance = 1.5): Track[] {
  const pool = [...tracks].sort((a, b) => trackStart(a) - trackStart(b));
  const result: Track[] = [];
  const consumed = new Set<Track>();
  for (const track of pool) {
    if (consumed.has(track)) continue;
    let current: Track = track;
    for (;;) {
      const tail = current.samples.at(-1)!;
      let best: Track | null = null;
      let bestDistance = Infinity;
      for (const other of pool) {
        if (other === track || consumed.has(other) || other.kind !== current.kind) continue;
        const gap = trackStart(other) - trackEnd(current);
        if (gap <= 0 || gap > maxGapSec) continue;
        const distance = relativeCenterDistance(tail.box, other.samples[0]!.box);
        if (distance <= maxRelativeDistance && distance < bestDistance) {
          best = other;
          bestDistance = distance;
        }
      }
      if (!best) break;
      consumed.add(best);
      current = { ...current, samples: [...current.samples, ...best.samples] };
    }
    result.push(current);
  }
  return result;
}

/** The sample of `track` closest in time to `t`. */
function nearestSample(track: Track, t: number): TrackSample {
  let best = track.samples[0]!;
  for (const sample of track.samples) if (Math.abs(sample.t - t) < Math.abs(best.t - t)) best = sample;
  return best;
}

/** How close to an existing sample a new one has to be to count as the same moment seen twice. */
const SAME_MOMENT_SEC = 0.5;

/**
 * Adds what a later scan of other parts of a video found to what earlier scans
 * found there. A new track that continues or overlaps a known one of the same
 * object joins it, so the person keeps their id (and whether they are shown);
 * the rest are added with `newId`. Returns every track and, per found track
 * id, the id it ended up under.
 */
export function mergeTracks(
  existing: readonly Track[],
  found: readonly Track[],
  newId: (track: Track) => string,
  maxGapSec = 2.5,
  maxRelativeDistance = 1.5,
): { readonly tracks: Track[]; readonly ids: ReadonlyMap<string, string> } {
  const tracks = [...existing];
  const ids = new Map<string, string>();
  for (const track of found) {
    let bestIndex = -1;
    let bestDistance = Infinity;
    tracks.forEach((candidate, index) => {
      // Only earlier finds: two new tracks were already told apart by this scan.
      if (index >= existing.length || candidate.kind !== track.kind) return;
      const from = Math.max(trackStart(candidate), trackStart(track));
      const to = Math.min(trackEnd(candidate), trackEnd(track));
      if (from - to > maxGapSec) return;
      // Where they overlap, or the edges facing each other across the gap.
      const at = (from + to) / 2;
      const distance = relativeCenterDistance(nearestSample(candidate, at).box, nearestSample(track, at).box);
      if (distance <= maxRelativeDistance && distance < bestDistance) {
        bestIndex = index;
        bestDistance = distance;
      }
    });
    if (bestIndex === -1) {
      const id = newId(track);
      ids.set(track.id, id);
      tracks.push({ ...track, id });
      continue;
    }
    const known = tracks[bestIndex]!;
    // Moments both scans saw stay as first found; only what is new is added.
    const fresh = track.samples.filter((sample) => Math.abs(nearestSample(known, sample.t).t - sample.t) > SAME_MOMENT_SEC);
    tracks[bestIndex] = { ...known, samples: [...known.samples, ...fresh].sort((a, b) => a.t - b.t) };
    ids.set(track.id, known.id);
  }
  return { tracks, ids };
}
